// The conversation with the consumer on one case (migration 047, rule 34): every email, from ActiveCampaign's
// log and from the feedback@ mailbox itself, in time order, AC's copy and feedback@'s of one email shown once.
// Pilot people only.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '../../lib/api';
import { fieldClass, labelClass, officeFocus, serverMessage, useResource } from '../../shells/OfficeShell';
import { byWhom, newKey, when, type PilotMessage, type PilotPayload } from './complaintsModel';

const card = 'flex flex-col gap-(--s3) rounded-md border border-(--border) bg-(--card) p-(--s4)';

/** An email's text; a long one (a reply carrying the whole thread below it) opens on request. */
export function EmailBody({ body }: { body: string | null }) {
  const text = body ?? 'No text.';
  const cls = 'm-0 whitespace-pre-line break-words text-(length:--fs-sm) leading-relaxed';
  const [open, setOpen] = useState(false);
  if (text.length <= 600) return <p className={cls}>{text}</p>;
  // Cut at a word, never inside one.
  const cut = text.slice(0, 600).replace(/\s+\S*$/, '');
  return (
    <div className="flex flex-col gap-(--s1)">
      <p className={cls}>{open ? text : `${cut}…`}</p>
      <button type="button" aria-expanded={open} className={`w-fit text-(length:--fs-sm) font-semibold underline ${officeFocus}`} onClick={() => setOpen((o) => !o)}>
        {open ? 'Show less' : 'Show the whole email'}
      </button>
    </div>
  );
}

/** Where the email came from, in words. */
function sourceLine(m: PilotMessage): string {
  if (m.automated) return 'Automated note · ActiveCampaign';
  const who = m.outbound ? 'Majans' : 'Consumer';
  if (m.source === 'APP') return `${who} · sent here by ${byWhom('APP', m.sentByUpn ?? null)}`;
  if (m.source === 'GRAPH') return `${who} · ${m.outbound ? 'from feedback@ in Outlook' : 'to feedback@'}`;
  return `${who} · ActiveCampaign`;
}

/** The emails with the consumer: every complaint reader's, read-only (QA team feedback, 2 Nov 2026). */
export function Conversation({ messages }: { messages: PilotPayload['messages'] }) {
  const msgs = messages;
  return (
    <section className={card} aria-labelledby="conversation-title">
      <div className="flex flex-wrap items-baseline justify-between gap-(--s2)">
        <h2 id="conversation-title" className="m-0 text-(length:--fs-sm) font-semibold">
          Emails with the consumer: {msgs.length}
        </h2>
      </div>
      {msgs.length ? (
        <ol className="m-0 flex list-none flex-col gap-(--s3) p-0">
          {msgs.map((m) => (
            <li key={m.id} className={`flex flex-col gap-(--s1) rounded-md border border-(--border) p-(--s3) ${m.outbound ? 'ml-(--s5) bg-(--subtle)' : 'mr-(--s5)'}`}>
              <span className="text-(length:--fs-xs) text-(--muted)">
                {sourceLine(m)} · {m.from ?? 'unknown sender'} · {when(m.sentAt)}
              </span>
              {m.subject ? <strong className="text-(length:--fs-sm)">{m.subject}</strong> : null}
              {m.automated ? null : <EmailBody body={m.body} />}
            </li>
          ))}
        </ol>
      ) : (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">No email on this case yet.</p>
      )}
    </section>
  );
}

interface UnmatchedPayload {
  mail: Array<{ id: number; from: string | null; subject: string | null; snippet: string | null; sentAt: string; hasAttachments: boolean }>;
  cases: Array<{ id: number; caseNo: string; consumerName: string | null; product: string | null; receivedAt: string }>;
}

/** Consumer emails in feedback@ that match no case: a person puts each on its case, or dismisses it. Pilot only. */
export function UnmatchedMail({ onAttached }: { onAttached: (said: string) => void }) {
  const data = useResource<UnmatchedPayload>('/complaints/mail/unmatched');
  const [pick, setPick] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<number | null>(null);
  const mail = data.data?.mail ?? [];
  if (data.error) return <p role="alert" className="m-0 text-(length:--fs-sm) text-(--warn)">Could not read the unmatched feedback@ emails: {data.error}</p>;
  if (!mail.length) return null;
  async function act(mailId: number, what: 'attach' | 'dismiss') {
    setBusy(mailId);
    setError(null);
    try {
      if (what === 'attach') {
        const complaintId = Number(pick[mailId]);
        await api.post(`/complaints/mail/${mailId}/attach`, { complaintId, clientKey: newKey() });
        onAttached(`The email is on ${data.data!.cases.find((c) => c.id === complaintId)?.caseNo ?? 'the case'} now.`);
      } else {
        await api.post(`/complaints/mail/${mailId}/dismiss`, { reason: 'not a complaint email', clientKey: newKey() });
        setConfirm(null);
        onAttached('Dismissed. It stays in feedback@.');
      }
      data.reload();
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(null);
    }
  }
  return (
    <section className={card} aria-labelledby="unmatched-title">
      <h2 id="unmatched-title" className="m-0 text-(length:--fs-sm) font-semibold">
        Emails in feedback@ not matched to a case: {mail.length}
      </h2>
      <p className="m-0 text-(length:--fs-sm) text-(--muted)">Put each on its case, or dismiss it.</p>
      <ol className="m-0 flex list-none flex-col gap-(--s3) p-0">
        {mail.map((m) => (
          <li key={m.id} className="flex flex-col gap-(--s2) rounded-md border border-(--border) p-(--s3)">
            <span className="text-(length:--fs-xs) text-(--muted)">
              {m.from ?? 'unknown sender'} · {when(m.sentAt)}
              {m.hasAttachments ? ' · has attachments' : ''}
            </span>
            {m.subject ? <strong className="text-(length:--fs-sm)">{m.subject}</strong> : null}
            {m.snippet ? <p className="m-0 whitespace-pre-line break-words text-(length:--fs-sm)">{m.snippet}</p> : null}
            <div className="flex flex-wrap items-end gap-(--s2)">
              <label className="flex flex-col gap-(--s1)">
                <span className={labelClass}>Case</span>
                <select className={`${fieldClass} ${officeFocus}`} value={pick[m.id] ?? ''} onChange={(e) => setPick((p) => ({ ...p, [m.id]: e.target.value }))}>
                  <option value="">Choose an open case</option>
                  {data.data!.cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.caseNo} · {c.consumerName ?? 'no name'} · {c.product ?? 'no product'}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="button" disabled={busy !== null || !pick[m.id]} onClick={() => void act(m.id, 'attach')}>
                {busy === m.id ? 'Working…' : 'Put on the case'}
              </Button>
              {confirm === m.id ? (
                <>
                  <Button type="button" variant="outline" disabled={busy !== null} onClick={() => void act(m.id, 'dismiss')}>
                    Yes, dismiss
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setConfirm(null)}>
                    Keep
                  </Button>
                </>
              ) : (
                <Button type="button" variant="outline" disabled={busy !== null} onClick={() => setConfirm(m.id)}>
                  Dismiss
                </Button>
              )}
            </div>
          </li>
        ))}
      </ol>
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {error}
        </p>
      ) : null}
    </section>
  );
}

type Phase = 'idle' | 'drafting' | 'approving' | 'sending';

/** The text as the server approves and sends it (domain/complaintReplies.ts normaliseReplyText), hashed the same way. */
async function replyHash(text: string): Promise<string> {
  const n = text
    .replace(/\r\n?/g, '\n')
    .replace(/\s*[–—]\s*/g, ', ')
    .split('\n')
    .map((l) => l.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, 6000);
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(n));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Write the next reply: Claude drafts it in the chosen template, the person edits it, a RELEASE holder
 * approves the exact text on a flagged case, and Send puts it in the consumer's thread from feedback@.
 * The address is the consumer's on the ActiveCampaign contact, read by the server at send time.
 */
export function Compose({
  caseId,
  pilot,
  templates,
  isQa,
  canSend,
  onSent,
}: {
  caseId: number;
  pilot: PilotPayload;
  templates: Array<{ key: string; label: string }>;
  isQa: boolean;
  canSend: boolean;
  onSent: (said: string) => void;
}) {
  const r = pilot.replies;
  const sug = pilot.suggestion;
  const sentAlready = pilot.messages.some((m) => m.outbound && !m.automated);
  const [template, setTemplate] = useState(r?.latestDraft?.template ?? sug?.template ?? 'ACKNOWLEDGE');
  const [text, setText] = useState(r?.latestDraft?.text ?? (sentAlready ? '' : (sug?.draftReply ?? '')));
  const [instructions, setInstructions] = useState('');
  const [approval, setApproval] = useState<{ id: number; text: string } | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const approvals = r?.approvals;
  useEffect(() => {
    if (!approvals?.length || !text) return;
    let live = true;
    void replyHash(text).then((h) => {
      const a = approvals.find((x) => x.textHash === h);
      if (live && a) setApproval((cur) => (cur?.text === text ? cur : { id: a.id, text }));
    });
    return () => {
      live = false;
    };
  }, [approvals, text]);
  if (!r) return null;
  const mode = pilot.mail?.replies ?? 'off';
  if (mode === 'off') {
    return <p className="m-0 text-(length:--fs-sm) text-(--muted)">Replying from the app is not on yet. Reply in ActiveCampaign, or from feedback@ in Outlook.</p>;
  }
  const approved = approval !== null && approval.text === text;
  const needsApproval = r.flagged && !approved;
  const hasText = /[\p{L}\p{N}]/u.test(text);
  const sendBlocked =
    !r.hasRecipient
        ? 'This case has no ActiveCampaign contact, so the app has no address to send to.'
        : needsApproval
          ? 'QA approves this text first.'
          : !canSend
            ? 'Only Rachel or QA can send.'
            : null;

  async function run(p: Phase, fn: () => Promise<void>): Promise<void> {
    setPhase(p);
    setError(null);
    setNote(null);
    try {
      await fn();
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setPhase('idle');
    }
  }
  const draft = () =>
    run('drafting', async () => {
      const res = await api.post<{ draft: { draftReply: string | null; reasoning: string | null; withheld: boolean } }>(`/complaints/${caseId}/draft`, {
        template,
        instructions: instructions || undefined,
      });
      const d = res.draft;
      if (d.draftReply) {
        setText(d.draftReply);
        setApproval(null);
      }
      setNote(d.withheld ? 'No draft: this case needs Quality to write the reply.' : (d.reasoning ?? 'Drafted. Read it and edit before you send.'));
    });
  const approve = () =>
    run('approving', async () => {
      const res = await api.post<{ approval: { id: number } }>(`/complaints/${caseId}/reply/approve`, { text, template, clientKey: newKey() });
      setApproval({ id: res.approval.id, text });
      setNote('Approved. Changing the text needs a new approval.');
    });
  const send = () =>
    run('sending', async () => {
      const res = await api.post<{ sent: boolean; uncertain?: boolean }>(`/complaints/${caseId}/reply/send`, {
        text,
        template,
        approvalId: approved ? approval!.id : undefined,
        clientKey: newKey(),
      });
      if (res.uncertain) {
        setNote('Graph did not confirm the send. The next sync checks feedback@ Sent Items; do not send it again yet.');
        return;
      }
      setText('');
      setApproval(null);
      onSent(r.test ? `Test sent to ${r.test} (not the consumer).` : "Sent from feedback@, in the consumer's thread.");
    });

  const busy = phase !== 'idle';
  return (
    <section className={card} aria-labelledby={`compose-${caseId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-(--s2)">
        <h2 id={`compose-${caseId}`} className="m-0 text-(length:--fs-sm) font-semibold">
          Reply to the consumer
        </h2>
        <span className="text-[12px] text-(--muted)">{r.awaitingUs ? 'the consumer wrote last' : 'from feedback@, in their thread'}</span>
      </div>
      {r.flagged ? (
        <div className="flex flex-col gap-(--s1) rounded-md bg-(--action-bg) px-(--s3) py-(--s2) text-(length:--fs-sm) text-(--action-fg)">
          <strong>{r.withheld ? 'No Claude draft: Quality writes this reply, and QA approves it.' : 'QA approves this exact text before it is sent.'}</strong>
          <span>{r.triggers.map((t) => t.label).join('; ')}.</span>
          {sug?.s131 ? <span>ACL s131: a death, serious injury or illness linked to the product must be reported to the ACCC within 2 days. QA answers it.</span> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-(--s2)">
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Kind of reply</span>
          <select className={`${fieldClass} ${officeFocus}`} value={template} onChange={(e) => setTemplate(e.target.value)} disabled={busy}>
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[220px] flex-1 flex-col gap-(--s1)">
          <span className={labelClass}>Anything Claude should say (optional)</span>
          <input
            className={`${fieldClass} ${officeFocus}`}
            maxLength={300}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            disabled={busy}
            placeholder="e.g. the return envelope went out today…"
          />
        </label>
        <Button type="button" variant="outline" disabled={busy || r.withheld} onClick={() => void draft()}>
          {phase === 'drafting' ? 'Drafting…' : 'Draft with Claude'}
        </Button>
      </div>
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>The email</span>
        <textarea
          className={`${fieldClass} ${officeFocus} min-h-[220px] leading-relaxed`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
          placeholder={r.withheld ? 'Write the reply…' : 'Draft with Claude, or write the reply…'}
        />
      </label>
      <p className="m-0 text-(length:--fs-xs) text-(--muted)">
        {r.test ? `Test mode: Send goes to ${r.test}, not the consumer.` : "To the consumer's address in ActiveCampaign."}
      </p>
      <div className="flex flex-wrap items-center gap-(--s2)">
        {r.flagged && isQa ? (
          <Button type="button" variant={approved ? 'outline' : 'default'} disabled={busy || !hasText || approved} onClick={() => void approve()}>
            {phase === 'approving' ? 'Approving…' : approved ? 'Approved' : 'Approve this text'}
          </Button>
        ) : null}
        <Button type="button" disabled={busy || !hasText || sendBlocked !== null} aria-describedby={`send-why-${caseId}`} onClick={() => void send()}>
          {phase === 'sending' ? 'Sending…' : r.test ? `Send test to ${r.test}` : 'Send from feedback@'}
        </Button>
        {sendBlocked ? (
          <span id={`send-why-${caseId}`} className="text-(length:--fs-sm) text-(--muted)">
            {sendBlocked}
          </span>
        ) : null}
      </div>
      {note ? <p className="m-0 text-(length:--fs-sm)">{note}</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {error}
        </p>
      ) : null}
    </section>
  );
}
